import InputMessage from '@features/common/components/InputMessage';
import InputText from '@features/common/components/InputText';
import PrimaryButton from '@features/common/components/PrimaryButton';
import TextArea from '@features/common/components/TextArea';
import { getCdnStaticUrl } from '@features/common/store/common.selectors';
import { unwrapResult } from '@reduxjs/toolkit';
import { useAppDispatch } from '@store/store';
import Image from 'next/image';
import Link from 'next/link';
import React, { MutableRefObject, ReactNode, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { IOrganizeState } from '../models/organize-state.model';
import { contactForm } from '../store/layout.action';
import { getSelectedLanguage } from '../store/layout.selectors';
import { gamyEmail } from '@utils/ui-constants';
import OrganizeStatistics from './OrganizeStatistics';
import OrganizeButtonScrollToForm from './OrganizeButtonScrollToForm';

type ISide = 'left' | 'right';

interface IOrganizeGamyProps {
	imageSrc: string[];
	title?: ReactNode;
	subtitle: ReactNode;
	showTermsLink?: boolean;
	switchSide?: ISide;
}

interface IContactFormProps {
	formWrapperRef: MutableRefObject<HTMLDivElement | null>;
}

const SubmitSuccess = () => {
	const { formatMessage } = useIntl();

	return (
		<div data-testid='form-successful' className='pt-14 md:mr-0 mr-10'>
			<p className='text-[#ff652e] text-4xl md:text-[32px] md:leading-[40px] xl:text-4.5xl  font-normal sm:w-full w-[100%] sm:text-left text-center sm:px-14 px-1.5 md:pr-[70px] md:pl-[70px] pt-14'>
				<strong>{formatMessage({ id: 'bcs-Organize-Success-Title' })}</strong>
			</p>
			<p className='text-3xl md:text-2xl xl:text-xl text-dark-600 font-normal sm:w-full w-[100%] sm:text-left text-center sm:px-14 px-1.5 md:pr-[70px] md:pl-[70px]'>
				{formatMessage({ id: 'bcs-Organize-Success-Description' })}
			</p>
		</div>
	);
};

const ContactForm = ({ formWrapperRef }: IContactFormProps) => {
	const dispatch = useAppDispatch();
	const { formatMessage } = useIntl();
	const [formStatus, setFormStatus] = useState(true);
	const { handleSubmit, formState, register, setError, clearErrors } = useForm<IOrganizeState>({
		mode: 'onBlur',
	});

	const onSubmitForm = (formForSubmit: IOrganizeState) => {
		dispatch(contactForm(formForSubmit))
			.then(unwrapResult)
			.then(() => {
				setFormStatus(false);
			})
			.catch(() => {
				setError('formError', { message: formatMessage({ id: 'bcs-Error-Form' }) });
			});
	};

	return (
		<div ref={formWrapperRef} className='flex bg-white flex-col md:flex-row py-5'>
			<div className='flex flex-col sm:w-1/2 w-full items-center'>
				<p className='text-2xl 2xl:text-4xl text-dark-600 font-normal lg:w-[60%] py-10 w-[100%] text-left pl-5 '>
					<strong>{formatMessage({ id: 'bcs-Organize-Send-Message' })}</strong>
				</p>
				<p className='lg:w-[60%] w-[100%] text-left pl-5'>
					<strong>{formatMessage({ id: 'bcs-Organize-Email' })}</strong>
				</p>
				<a
					href={`mailto:${gamyEmail}`}
					className='lg:w-[60%] w-[100%] sm:pb-0 pb-[20px] text-left pl-5 underline'
				>
					{gamyEmail}
				</a>
			</div>
			<form
				onSubmit={handleSubmit(onSubmitForm)}
				className={`flex flex-col space-y-5 rounded-3xl mr-5 ml-5 p-5 ${
					!formStatus ? 'h-[894px] w-[100vw] sm:w-[570px]' : ''
				}`}
				data-testid='form'
			>
				{formStatus ? (
					<>
						<div className='flex'>
							<div className='w-1/2'>
								<InputText
									id={'name'}
									label={formatMessage({ id: 'bcs-Organize-Form-Name' })}
									type={'text'}
									testId={'name'}
									tabIndex={1}
									fieldError={formState.errors.name}
									placeholder={formatMessage({ id: 'bcs-Organize-Form-Name' })}
									hookRegister={register('name', {
										required: {
											value: true,
											message: formatMessage({
												id: 'bcs-Error-Name',
											}),
										},
									})}
								/>
							</div>

							<div className='pr-3.5' />
							<div className='w-1/2'>
								<InputText
									id={'surname'}
									label={formatMessage({ id: 'bcs-Organize-Form-Surname' })}
									type={'text'}
									testId={'surname'}
									tabIndex={2}
									fieldError={formState.errors.surname}
									placeholder={formatMessage({ id: 'bcs-Organize-Form-Surname' })}
									hookRegister={register('surname')}
								/>
							</div>
						</div>
						<InputText
							id={'organization'}
							label={formatMessage({ id: 'bcs-Organize-Form-Organization' })}
							type={'text'}
							testId={'organization'}
							tabIndex={3}
							fieldError={formState.errors.organization}
							placeholder={formatMessage({ id: 'bcs-Organize-Form-Organization' })}
							hookRegister={register('organization', {
								required: {
									value: true,
									message: formatMessage({
										id: 'bcs-Error-Organization',
									}),
								},
							})}
						/>
						<InputText
							id={'position'}
							label={formatMessage({ id: 'bcs-Organize-Form-Position' })}
							type={'text'}
							testId={'position'}
							tabIndex={4}
							fieldError={formState.errors.position}
							placeholder={formatMessage({ id: 'bcs-Organize-Form-Position' })}
							hookRegister={register('position')}
						/>
						<InputText
							id={'email'}
							label={formatMessage({ id: 'bcs-Organize-Form-Email' })}
							type={'email'}
							testId={'email'}
							tabIndex={5}
							fieldError={formState.errors.email}
							placeholder={formatMessage({ id: 'bcs-Organize-Form-Email' })}
							hookRegister={register('email', {
								required: {
									value: true,
									message: formatMessage({
										id: 'bcs-Error-EmptyEmail',
									}),
								},
							})}
						/>
						<TextArea
							id={'message'}
							label={formatMessage({ id: 'bcs-Organize-Form-Message' })}
							testId={'message'}
							tabIndex={6}
							fieldError={formState.errors.message}
							limit={2000}
							value={''}
							clearErrors={(event: any) => clearErrors(event)}
							placeholder={formatMessage({ id: 'bcs-Organize-Form-Message' })}
							hookRegister={register('message', {
								required: {
									value: true,
									message: formatMessage({
										id: 'bcs-Error-Message',
									}),
								},
							})}
						/>
						<p>
							{formatMessage(
								{ id: 'bcs-Organize-Form-Term' },
								{
									linkPolicy: (
										<a href='/privacy-policy' target='_blank' className='underline'>
											{formatMessage({ id: 'bcs-Organize-Form-TermPrivacyPolicy' })}
										</a>
									),
								}
							)}
						</p>
						<div className='w-full flex'>
							<div className='w-[40%]'>
								<PrimaryButton
									type='submit'
									testId='sendMessageBtn'
									text={formatMessage({ id: 'bcs-Organize-Form-Button' })}
									tabIndex={7}
								/>
							</div>
							<div className='w-[60%] flex mb:mb-auto mt-auto mb-[25px]'>
								<InputMessage inputTestId={'formError'} fieldError={formState.errors.formError} />
							</div>
						</div>
					</>
				) : (
					<SubmitSuccess />
				)}
			</form>
		</div>
	);
};

const OrganizeBox = ({ imageSrc, title, subtitle, showTermsLink, switchSide }: IOrganizeGamyProps) => {
	const { formatMessage } = useIntl();
	const cdnStaticUrl = useSelector(getCdnStaticUrl);

	const selectedLanguage = useSelector(getSelectedLanguage);

	const getImageSrc = (): string => {
		const selectedImageByLanguage: string | undefined = imageSrc.find((imageName) =>
			imageName.includes(selectedLanguage)
		);

		if (selectedImageByLanguage !== undefined) {
			return `${cdnStaticUrl}/${selectedImageByLanguage}`;
		}

		return '';
	};

	return (
		<div
			className={`flex flex-col ${
				switchSide === 'right' ? 'lg:flex-row  bg-white' : 'lg:flex-row-reverse bg-grayBlue-400'
			} lg:flex-row justify-between items-center px-5 md:px-[4.5rem] space-y-3 md:space-y-0 md:space-x-13 pt-8 pb-[30px] md:py-8`}
		>
			<div className='w-full'>
				<div
					className='rounded-3xl aspect-w-15 aspect-h-11 overflow-hidden flex-1 relative min-w-[200px] min-h-full sm:min-w-[543px] sm:min-h-[398px]'
					style={{
						boxShadow: 'rgba(0, 0, 0, 0.5) 3px 3px 10px',
					}}
				>
					<Image
						alt='organize-image'
						unoptimized={true}
						src={getImageSrc()}
						layout={'fill'}
						objectFit={'fill'}
						priority={true}
					/>
				</div>
			</div>

			<div className='w-full flex justify-center text-start'>
				{title ? (
					<p
						className='text-3xl 2xl:text-4.5xl font-semibold text-dark-600'
						style={{ fontFamily: "'Montserrat', normal" }}
					>
						{title}
					</p>
				) : null}
				<p className='2xl:w-[51%] xl:w-[60%] w-full text-2xl lg:text-3xl 2xl:text-4xl xl:text-3xl text-dark-600 font-normal lg:mt-0 mt-[30px]'>
					{subtitle}{' '}
					{showTermsLink ? (
						<Link href='/terms-and-conditions'>
							<span className='underline cursor-pointer'>
								{formatMessage({ id: 'bcs-HowGamyWorks-CompeteDescriptionTerms' })}
							</span>
						</Link>
					) : null}
				</p>
			</div>
		</div>
	);
};

const Organize = () => {
	const { formatMessage } = useIntl();
	const formWrapperRef = useRef<HTMLDivElement | null>(null);
	const cdnStaticUrl = useSelector(getCdnStaticUrl);

	const scrollToForm = () => {
		formWrapperRef?.current?.scrollIntoView({ behavior: 'smooth' });
	};

	return (
		<div className='mt-18'>
			<div className='w-full relative'>
				<div className='aspect-w-9 aspect-h-12 s:aspect-w-11 s:aspect-h-10 sm:aspect-w-13 sm:aspect-h-9 lg:aspect-w-13 lg:aspect-h-6 xl:aspect-w-15 xl:aspect-h-6 1xl:aspect-w-16 1xl:aspect-h-7'>
					<Image
						src={`${cdnStaticUrl}/header-bg.jpg`}
						layout='fill'
						objectFit='cover'
						quality={100}
						unoptimized={true}
						priority={true}
						alt='header background'
					/>
				</div>
				<div
					className='absolute inset-0 flex flex-col justify-center items-center px-5 space-y-5 1xl:space-y-12 xs:space-y-8 xs:pt-0 xs:pb-0 pt-[30px] pb-[50px]  text-white md:-mt-13 1xl:-mt-20'
					style={{
						fontFamily: 'Montserrat',
					}}
				>
					<h1 className='text-[28px] leading-[34px] md:text-[45px] 1xl:text-[56px] md:leading-[62px] 1xl:leading-[66px] md:tracking-[-3px] font-bold text-center'>
						<span className='hidden md:block'>
							{formatMessage({ id: 'bcs-Organize-Gamy-Title' }, { br: <br /> })}
						</span>
						<span className='block md:hidden xs:text-[28px] xs:leading-8 text-xlg'>
							{formatMessage({ id: 'bcs-Organize-Gamy-Title' }, { br: ' ' })}
						</span>
					</h1>
					<ul className='flex flex-col justify-center items-center space-y-3 1xl:space-y-4 font-semibold xs:text-xsm text-[0.8rem] md:text-[18px] md:leading-5 px-0 text-center'>
						<li>{formatMessage({ id: 'bcs-Organize-Gamy-SubTitle-1' })}</li>
						<li>{formatMessage({ id: 'bcs-Organize-Gamy-SubTitle-2' })}</li>
						<li>{formatMessage({ id: 'bcs-Organize-Gamy-SubTitle-3' })}</li>
					</ul>
					<OrganizeButtonScrollToForm
						scrollToForm={scrollToForm}
						translationKey={'bcs-Organize-Gamy-Button'}
					/>
				</div>
				<div className='absolute bottom-0 left-0 right-0 flex justify-center mb-2 1xl:mb-6 animate-bounce'>
					<Link href={'#organize-box'} scroll={true}>
						<a>
							<svg
								width='20'
								height='40'
								className='w-[10px] h-[20px] sm:w-4 sm:h-8 md:w-5 md:h-10'
								viewBox='0 0 20 40'
								fill='none'
								xmlns='http://www.w3.org/2000/svg'
							>
								<path
									d='M9.85709 0C4.41286 0 0 4.41316 0 9.85709V19.2193C0 23.261 2.47473 26.7668 5.98017 28.2927V25.117C4.08284 23.8385 2.80438 21.6938 2.80438 19.2193V9.85717C2.80438 5.98025 5.98017 2.80446 9.85709 2.80446C13.7751 2.80446 16.9098 5.98025 16.9098 9.85717V19.2193C16.9098 21.6941 15.6313 23.8385 13.734 25.117V28.2927C17.2398 26.7668 19.7142 23.3022 19.7142 19.2193V9.85709C19.7142 4.41316 15.3013 0 9.85709 0Z'
									fill='white'
								/>
								<path
									d='M9.85727 12.4581C10.641 12.4581 11.2595 11.8396 11.2595 11.0559V7.26157C11.2595 6.47787 10.6409 5.85938 9.85727 5.85938C9.07361 5.85938 8.45508 6.4779 8.45508 7.26157V11.0559C8.45508 11.7982 9.07361 12.4581 9.85727 12.4581Z'
									fill='white'
								/>
								<path
									d='M13.6103 32.086L11.2593 34.437V22.1053C11.2593 21.3216 10.6408 20.7031 9.85714 20.7031C9.07348 20.7031 8.45495 21.3217 8.45495 22.1053V34.3958L6.10395 32.0448C5.5677 31.5085 4.66029 31.5085 4.12435 32.0448C3.58811 32.581 3.58811 33.4884 4.12435 34.0243L8.86716 38.7671C9.156 39.056 9.48569 39.1797 9.85707 39.1797C10.2282 39.1797 10.5582 39.056 10.847 38.7671L15.5898 34.0243C16.126 33.4881 16.126 32.5807 15.5898 32.0448C15.0124 31.5088 14.1461 31.5088 13.6102 32.0862L13.6103 32.086Z'
									fill='#FF652E'
								/>
							</svg>
						</a>
					</Link>
				</div>
			</div>
			<OrganizeStatistics scrollToForm={scrollToForm} />
			<p
				style={{ fontFamily: 'Montserrat' }}
				className='sm:mt-6 mt-1 mb-4 font-bold text-center text-3xl text-dark-800 sm:tracking-[-0.06rem] tracking-[-0.19rem]'
			>
				{formatMessage({ id: 'bcs-Organize-How-It-Works-Section' })}
			</p>
			<div>
				<OrganizeBox
					imageSrc={['organizeQuizzes_EN.jpg', 'organizeQuizzes_BG.jpg']}
					subtitle={formatMessage(
						{ id: 'bcs-Organize-Trivia-Quizzes' },
						{ triviaQuizzes: <strong>{formatMessage({ id: 'bcs-Organize-Trivia-QuizzesBold' })}</strong> }
					)}
					switchSide='right'
				/>
			</div>
			<div>
				<OrganizeBox
					imageSrc={['organizePromotion_EN.jpg', 'organizePromotion_BG.jpg']}
					subtitle={formatMessage(
						{ id: 'bcs-Organize-Promotion' },
						{ yourBusiness: <strong>{formatMessage({ id: 'bcs-Organize-PromotionBusiness' })}</strong> }
					)}
					switchSide='left'
				/>
			</div>
			<div>
				<OrganizeBox
					imageSrc={['organizeLimitedAccessGamy_EN.jpg', 'organizeLimitedAccessGamy_BG.jpg']}
					subtitle={formatMessage(
						{ id: 'bcs-Organize-Limited-Access-Information' },
						{
							limitedAccessInformation: (
								<strong>{formatMessage({ id: 'bcs-Organize-Limited-Access-InformationBold' })}</strong>
							),
						}
					)}
					switchSide='right'
				/>
			</div>
			<div>
				<OrganizeBox
					imageSrc={['organizeRanking_EN.jpg', 'organizeRanking_BG.jpg']}
					subtitle={formatMessage(
						{ id: 'bcs-Organize-Awards' },
						{ awards: <strong>{formatMessage({ id: 'bcs-Organize-AwardsBold' })}</strong> }
					)}
					switchSide='left'
				/>
			</div>
			<div>
				<OrganizeBox
					imageSrc={['organizePodium_EN.jpg', 'organizePodium_BG.jpg']}
					subtitle={formatMessage(
						{ id: 'bcs-Organize-Brand' },
						{ yourBrand: <strong>{formatMessage({ id: 'bcs-Organize-BrandYourBrand' })}</strong> }
					)}
					switchSide='right'
				/>
			</div>
			<div className='w-full h-[3px] bg-grayBlue-400 mb-[30px] mt-13' />
			<ContactForm formWrapperRef={formWrapperRef} />
		</div>
	);
};

export default Organize;
